import { useEffect, useState } from "react";
import {
  BaseModal,
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
  Input,
  Label,
} from "@src/common/components";
import CourieButton from "@src/common/components/Button/Button";
import {
  TripDistanceData,
  TripStopInfo,
} from "@src/common/components/TripDistancesTable";
import { useCourierLocale } from "@src/common/courierLocale";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { useCourieStore } from "@src/common/lib/store";
import { milesToMeters } from "@src/common/lib/NumberUtils";
import { useOverrideOrderLegDistanceMutation } from "@api/graphql/generated/generated-types";
import { ApolloError } from "@apollo/client";

interface GlobalMileageOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  trip?: TripDistanceData;
  onSaved: () => void;
}

function StopSummary({ label, stop }: { label: string; stop: TripStopInfo }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      {stop.companyName && (
        <div className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
          {stop.companyName}
        </div>
      )}
      <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
        {stop.address}
      </div>
    </div>
  );
}

export function GlobalMileageOverrideModal({
  isOpen,
  onClose,
  orderId,
  trip,
  onSaved,
}: GlobalMileageOverrideModalProps) {
  const { courierId } = useAuth();
  const { distanceLabel, displayToMiles } = useCourierLocale();
  const { showToast } = useCourieStore();
  const showError = useErrorToast();
  const [distance, setDistance] = useState("");
  const [prefilledDistance, setPrefilledDistance] = useState("");

  const [overrideOrderLegDistance, { loading }] =
    useOverrideOrderLegDistanceMutation();

  useEffect(() => {
    if (!isOpen || !trip) {
      setDistance("");
      setPrefilledDistance("");
      return;
    }
    const initialValue =
      trip.displayDistance != null && Number.isFinite(trip.displayDistance)
        ? String(trip.displayDistance)
        : "";
    setDistance(initialValue);
    setPrefilledDistance(initialValue);
  }, [isOpen, trip]);

  if (!trip) return null;

  const parsedDistance = Number(distance);
  // >= 0, not > 0: a zeroed leg (e.g. a flight segment that shouldn't be
  // billed by mileage) is a real case this feature exists for.
  const isDistanceValid =
    distance.trim() !== "" &&
    Number.isFinite(parsedDistance) &&
    parsedDistance >= 0;

  function handleSave() {
    if (
      !isDistanceValid ||
      !courierId ||
      !trip?.sourceStopId ||
      !trip?.destinationStopId
    )
      return;
    // If the user didn't touch the prefilled (rounded) value, submit the
    // trip's exact distance rather than the rounded-then-reconverted one —
    // otherwise saving without editing silently nudges pricing and driver
    // pay by the rounding error. (displayDistance itself never rounds a
    // nonzero distance down to "0" — see ShipmentDistanceModal — so a typed
    // 0 always means the user intends to zero this leg.)
    const distanceInMeters =
      distance === prefilledDistance && trip.distanceInMeters != null
        ? trip.distanceInMeters
        : milesToMeters(displayToMiles(parsedDistance));

    overrideOrderLegDistance({
      variables: {
        courierId,
        orderId,
        originStopId: trip.sourceStopId,
        destinationStopId: trip.destinationStopId,
        distanceInMeters,
      },
    })
      .then(() => {
        showToast({
          message: "Global mileage override saved",
          type: "success",
        });
        onSaved();
        onClose();
      })
      .catch((err) => showError(err as ApolloError));
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      size="default"
      overrides={{
        Root: {
          style: {
            zIndex: 10000,
          },
        },
      }}
    >
      <BaseModalHeader>Create Global Mileage Override</BaseModalHeader>
      <BaseModalBody>
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100">
            This distance will become the default for every order between these
            two locations. It will affect customer pricing and driver pay
            wherever mileage is used.
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <StopSummary label="From" stop={trip.origin} />
            <StopSummary label="To" stop={trip.destination} />
          </div>

          <div>
            <Label htmlFor="global-mileage-override-distance">
              Override distance ({distanceLabel})
            </Label>
            <Input
              id="global-mileage-override-distance"
              type="number"
              min={0}
              step={0.1}
              value={distance}
              placeholder={`Enter distance in ${distanceLabel}`}
              onChange={(e) =>
                setDistance((e.target as HTMLInputElement).value)
              }
            />
          </div>
        </div>
      </BaseModalBody>
      <BaseModalFooter>
        <div className="flex w-full justify-end gap-2">
          <CourieButton color="gray" onClick={onClose}>
            Cancel
          </CourieButton>
          <CourieButton
            color="info"
            disabled={!isDistanceValid}
            isProcessing={loading}
            onClick={handleSave}
          >
            Save global override
          </CourieButton>
        </div>
      </BaseModalFooter>
    </BaseModal>
  );
}
